// ═══════════════════════════════════════════════════════════════════════════
// drpBoardHealth.js — is the DRP board we are about to decide on actually the
// current one? Four states, and a decision may only claim "today" in one.
//
// ── THE DEFECT THIS EXISTS FOR (measured 2026-09-01, live production) ───────
//
// The snapshot writer called drpDecide with `asOf: globalLatest, boardDate:
// globalLatest` — BOTH taken from the board's own newest row. So
// `boardAgeDays = asOf - boardDate` was structurally **always 0** and the
// stale-board gate could never fire. It was live code that could not run.
//
// What that produced, straight out of drp_decision_daily:
//
//   snapshot_date   board_date    board_age_days   decisions emitted
//   2026-08-28      2026-08-28    0                BUY,START,WAIT,WATCH
//   2026-08-29      2026-08-28    0                BUY,START,WAIT,WATCH   <- Sat
//   2026-08-30      2026-08-28    0                BUY,START,WAIT,WATCH   <- Sun
//   2026-08-31      2026-08-28    0                BUY,START,WAIT,WATCH   <- Mon
//   2026-09-01      2026-08-31    0                START,WAIT
//
// Three consecutive days presented Friday's board as a current, actionable read
// — including on a Sunday — and reported its age as zero. The cron time (05:00
// UTC against a board that lands 19:20-20:10 UTC) is the smaller half of this:
// the larger half is that `asOf` was derived from the data instead of the clock,
// so no schedule could have fixed it.
//
// ── WHY "TODAY'S BOARD MUST EXIST" IS THE WRONG INVARIANT ───────────────────
//
// Measured over 2026-06-01..2026-08-31: the board is missing on **15 of 66
// weekdays (23%)**, including five consecutive business days (Jul 1,2,3,6,7)
// and a three-day run (Jul 22,23,24). Requiring today's board would blank the
// surface on a quarter of all days.
//
// The gaps are NOT all RMA. Comparing against the raw ADM feed, **four days
// exist in `drp_adm_daily_price` and NOT in `drp_board`** (07-01, 07-06, 07-07,
// 07-23) — our own promotion failed there, silently, because
// **`_drp-adm-sync` writes NO `sync_runs` row** (verified: the only %drp%/%adm%
// source is `adm-basis`, an unrelated wheat-basis scraper). There is no job
// health signal for this feed at all, so health has to be measured from the
// DATA. Whether the remaining 11 gaps are RMA not publishing or our sync also
// missing them is NOT established, and this module does not pretend otherwise —
// it reports UNKNOWN rather than inventing a cause.
//
// So the invariant is stated in TRADING SESSIONS, not calendar days, and it
// degrades rather than blanking:
//
//   CURRENT  — the board is the newest session we could reasonably expect.
//   DELAYED  — one expected session behind. Decisions still render, but they
//              may NOT claim to be today's and must not raise "act now" alerts.
//   STALE    — two or more sessions behind. The decision surface is withheld.
//   UNKNOWN  — we cannot even say what session to expect (no board at all, a
//              future-dated board, a clock we do not trust).
//
// PURE. No fetch, no Date.now() — `asOf` is always an argument, because the
// whole defect above was a date that came from the wrong place.
// ═══════════════════════════════════════════════════════════════════════════

const BOARD_HEALTH = { CURRENT: "CURRENT", DELAYED: "DELAYED", STALE: "STALE", UNKNOWN: "UNKNOWN" };

// ── the publication window, MEASURED ───────────────────────────────────────
// min(updated_at) per price_date, 2026-07-01..2026-08-31: every single board
// day landed between 19:20 and 20:10 UTC, on the same calendar day as its
// price_date (0 rows written on a different day across 33 days). Earlier months
// are excluded from this window deliberately: May 2026 rows were written in a
// backfill at 23:26-23:29 UTC, days after their price_date, and treating a
// backfill as evidence of a publication schedule is how a cron gets aimed at a
// time nothing ever happens.
//
// 19:20-20:10 UTC is 15:20-16:10 EDT. Under EST the same local event lands an
// hour later in UTC, so a schedule keyed to UTC needs BOTH hours — the DST rule
// this repo already applies to the NASS crons.
const BOARD_WINDOW_UTC = { earliestHour: 19, latestHour: 20, latestMinute: 10, dstShiftHours: 1 };

// Federal holidays — the only days we can say IN ADVANCE that no session is
// expected. Encoded as a rule, never as a measured list: baking in the observed
// gaps would make our own sync outages permanent "holidays". Same reasoning as
// the futures calendar in check-home-safe.
function nthWeekdayOfMonth(y, m, weekday, n) {
  const first = new Date(Date.UTC(y, m - 1, 1));
  const shift = (weekday - first.getUTCDay() + 7) % 7;
  return new Date(Date.UTC(y, m - 1, 1 + shift + (n - 1) * 7));
}
function lastWeekdayOfMonth(y, m, weekday) {
  const last = new Date(Date.UTC(y, m, 0));
  const shift = (last.getUTCDay() - weekday + 7) % 7;
  return new Date(Date.UTC(y, m - 1, last.getUTCDate() - shift));
}
const iso = (d) => d.toISOString().slice(0, 10);

// A Saturday holiday is observed the Friday before; a Sunday holiday the Monday
// after. Applied only to FIXED-DATE holidays — the floating ones already fall on
// a weekday by construction.
function observed(y, m, d) {
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay();
  if (dow === 6) return iso(new Date(Date.UTC(y, m - 1, d - 1)));
  if (dow === 0) return iso(new Date(Date.UTC(y, m - 1, d + 1)));
  return iso(dt);
}
function federalHolidays(year) {
  const H = new Set();
  H.add(observed(year, 1, 1));                                   // New Year's Day
  H.add(iso(nthWeekdayOfMonth(year, 1, 1, 3)));                   // MLK — 3rd Mon Jan
  H.add(iso(nthWeekdayOfMonth(year, 2, 1, 3)));                   // Washington — 3rd Mon Feb
  H.add(iso(lastWeekdayOfMonth(year, 5, 1)));                     // Memorial — last Mon May
  if (year >= 2021) H.add(observed(year, 6, 19));                 // Juneteenth (federal from 2021)
  H.add(observed(year, 7, 4));                                    // Independence Day
  H.add(iso(nthWeekdayOfMonth(year, 9, 1, 1)));                   // Labor — 1st Mon Sep
  H.add(iso(nthWeekdayOfMonth(year, 10, 1, 2)));                  // Columbus — 2nd Mon Oct
  H.add(observed(year, 11, 11));                                  // Veterans Day
  H.add(iso(nthWeekdayOfMonth(year, 11, 4, 4)));                  // Thanksgiving — 4th Thu Nov
  H.add(observed(year, 12, 25));                                  // Christmas
  return H;
}
const holidayCache = new Map();
function isHoliday(dateStr) {
  const y = Number(dateStr.slice(0, 4));
  if (!holidayCache.has(y)) holidayCache.set(y, federalHolidays(y));
  return holidayCache.get(y).has(dateStr);
}
function isSessionDay(dateStr) {
  const dow = new Date(`${dateStr}T00:00:00Z`).getUTCDay();
  if (dow === 0 || dow === 6) return false;
  return !isHoliday(dateStr);
}
const addDays = (dateStr, n) => iso(new Date(Date.parse(`${dateStr}T00:00:00Z`) + n * 86400000));

// The newest session whose board we could reasonably expect to hold, given the
// clock. Before the publication window closes on a session day, that session's
// board is not late — it simply has not been published yet, so the expectation
// is the PREVIOUS session.
function expectedSession(asOf, nowUtcHour) {
  if (!asOf) return null;
  let d = asOf;
  const windowClosed = nowUtcHour == null
    ? true // no clock hour supplied: assume the window has closed (conservative —
           // it can only make us call a board DELAYED, never falsely CURRENT)
    : nowUtcHour >= (BOARD_WINDOW_UTC.latestHour + BOARD_WINDOW_UTC.dstShiftHours);
  if (isSessionDay(d) && !windowClosed) d = addDays(d, -1);
  let guard = 0;
  while (!isSessionDay(d) && guard++ < 15) d = addDays(d, -1);
  return guard >= 15 ? null : d;
}
// How many sessions separate two dates (exclusive of `from`, inclusive of `to`).
function sessionsBetween(from, to) {
  if (!from || !to || from > to) return 0;
  let n = 0, d = from, guard = 0;
  while (d < to && guard++ < 400) { d = addDays(d, 1); if (isSessionDay(d)) n++; }
  return n;
}

// ── the health read ────────────────────────────────────────────────────────
// `asOf` is the RUN DATE (the clock), `boardDate` the newest board row. They are
// different things and conflating them is the defect this file documents.
function boardHealth({ asOf, boardDate, nowUtcHour, maxDelayedSessions = 1, promotionFailed = false }) {
  const base = { asOf: asOf || null, boardDate: boardDate || null, expectedSession: null, sessionsBehind: null };
  if (!asOf || !boardDate) {
    return { ...base, state: BOARD_HEALTH.UNKNOWN, actionable: false, mayClaimToday: false,
      reason: !boardDate ? "no DRP board rows at all" : "no run date supplied" };
  }
  if (boardDate > asOf) {
    return { ...base, state: BOARD_HEALTH.UNKNOWN, actionable: false, mayClaimToday: false,
      reason: `board is dated ${boardDate}, ahead of the run date ${asOf} — ingest clock error` };
  }
  const exp = expectedSession(asOf, nowUtcHour);
  if (!exp) {
    return { ...base, state: BOARD_HEALTH.UNKNOWN, actionable: false, mayClaimToday: false,
      reason: "cannot determine the expected trading session" };
  }
  const behind = sessionsBetween(boardDate, exp);
  const out = { ...base, expectedSession: exp, sessionsBehind: behind, promotionFailed: !!promotionFailed };
  // ⚠ A PROVEN PROMOTION FAILURE OUTRANKS THE DATE ARITHMETIC. Upstream rows
  // landed and nothing was promoted, so the board can be current by date and
  // wrong by content. Measured 2026-07-01/06/07/23: 250 upstream price rows
  // each, zero board rows, and the dates were never revisited.
  if (promotionFailed) {
    return { ...out, state: BOARD_HEALTH.STALE, actionable: false, mayClaimToday: false,
      reason: `a promotion failure is recorded for the current board window (board ${boardDate})` };
  }
  if (behind <= 0) {
    return { ...out, state: BOARD_HEALTH.CURRENT, actionable: true, mayClaimToday: true,
      reason: `board is the newest expected session (${boardDate})` };
  }
  if (behind <= maxDelayedSessions) {
    return { ...out, state: BOARD_HEALTH.DELAYED, actionable: true, mayClaimToday: false,
      reason: `board is ${behind} trading session behind — newest expected is ${exp}, we hold ${boardDate}` };
  }
  return { ...out, state: BOARD_HEALTH.STALE, actionable: false, mayClaimToday: false,
    reason: `board is ${behind} trading sessions behind — newest expected is ${exp}, we hold ${boardDate}` };
}

module.exports = {
  BOARD_HEALTH, BOARD_WINDOW_UTC,
  federalHolidays, isHoliday, isSessionDay, expectedSession, sessionsBetween, boardHealth,
};
